import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL } from './utils/authUtils';
import { devLogSafe, devLog, devError } from './utils/secureLogger';

const BACKEND_URL = API_URL;

const RecruiterRegistrationForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const updateToken = searchParams.get('token');
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [recruiterId, setRecruiterId] = useState(null);
  const [isLoggedInRecruiter, setIsLoggedInRecruiter] = useState(false);
  const states = ['NA/Remote', 'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];
  
  const majors = [
    'Aerospace Engineering',
    'Biomedical Engineering',
    'Biological Engineering',
    'Chemical Engineering',
    'Civil Engineering',
    'Computer Engineering',
    'Computer Science',
    'Electrical Engineering',
    'Environmental Engineering',
    'Industrial/Systems/Operations Engineering',
    'Materials Engineering',
    'Mechanical Engineering',
    'Petroleum Engineering',
    'Systems Engineering'
  ];
  
  const classYears = [
    'College - 1st Year',
    'College - 2nd Year',
    'College - 3rd Year',
    'College - 4th Year',
    'College - 5th Year'
  ];
  
  const institutions = [
    'CALIF STATE UNIV-SACRAMENTO',
    'CORNELL UNIVERSITY',
    'GEORGE MASON UNIVERSITY',
    'GEORGIA INSTITUTE OF TECHNOLOGY',
    'KANSAS STATE UNIVERSITY',
    'KETTERING UNIVERSITY',
    'LEHIGH UNIVERSITY',
    'LOUISIANA STATE UNIVERSITY & A&M COLG',
    'MICHIGAN TECHNOLOGICAL UNIV',
    'MILWAUKEE SCHOOL OF ENGINEERING',
    'MORGAN STATE UNIVERSITY',
    'NEW JERSEY INST OF TECHNOLOGY',
    'NORTH CAROLINA A&T STATE UNIV',
    'NORTHEASTERN UNIVERSITY',
    'PENNSYLVANIA STATE UNIVERSITY (THE)',
    'PURDUE UNIVERSITY, WEST LAFAYETTE',
    'RUTGERS, THE STATE UNIVERSITY OF NJ',
    'STEVENS INSTITUTE OF TECHNOLOGY',
    'TEXAS A&M UNIVERSITY',
    'TUSKEGEE UNIVERSITY',
    'UNIVERSITY OF ARKANSAS AT FAYETTEVILLE',
    'UNIVERSITY OF CALIFORNIA-DAVIS',
    'UNIVERSITY OF CALIFORNIA-SAN DIEGO',
    'UNIVERSITY OF CINCINNATI',
    'UNIVERSITY OF COLORADO BOULDER',
    'UNIVERSITY OF DAYTON',
    'UNIVERSITY OF DELAWARE',
    'UNIVERSITY OF GEORGIA',
    'UNIVERSITY OF ILLINOIS URBANA-CHAMPAIGN',
    'UNIVERSITY OF KENTUCKY',
    'UNIVERSITY OF MICHIGAN - ANN ARBOR',
    'UNIVERSITY OF NEBRASKA-LINCOLN',
    'UNIVERSITY OF SOUTHERN CALIFORNIA',
    'UNIVERSITY OF TEXAS SAN ANTONIO',
    'UNIVERSITY OF WASHINGTON'
  ];

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    organization: '',
    title: '',
    phone: '',
    company_description: '',
    board_company: '',
    corp_scholar_participant: '',
    webinar_host: '',
    internship_type: '',
    internship_description: '',
    paid_summer_internship: '',
    expected_start_date: '',
    internship_length: '',
    housing_provided: '',
    internship_salary: '',
    salary_type: '',
    minimum_gpa: '',
    number_of_references: '',
    essay_question: '',
    external_application_link: '',
    evergreen_link: '',
    expect_evergreen_link: '',
  });

  const [selectedStates, setSelectedStates] = useState({});
  const [stateCities, setStateCities] = useState({});
  const [selectedMajors, setSelectedMajors] = useState({});
  const [selectedInstitutions, setSelectedInstitutions] = useState({});
  const [selectedClassYears, setSelectedClassYears] = useState({});
  const [showLocations, setShowLocations] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    if (updateToken) {
      loadRecruiterData();
    } else {
      // Check if recruiter is logged in
      const recruiterToken = localStorage.getItem('recruiterToken');
      const recruiterInfo = localStorage.getItem('recruiterInfo');
      
      if (recruiterToken && recruiterInfo) {
        try {
          const recruiter = JSON.parse(recruiterInfo);
          setIsLoggedInRecruiter(true);
          loadRecruiterDataById(recruiter.id);
        } catch (error) {
          console.error('Error parsing recruiter info:', error);
          setInitialLoading(false);
        }
      } else {
        setInitialLoading(false);
      }
    }
  }, [updateToken]);

  const populateFormWithRecruiterData = (recruiter) => {
    // Populate form data
    setFormData({
      full_name: recruiter.name || '',
      email: recruiter.email || '',
      organization: recruiter.organization || '',
      title: recruiter.title || '',
      phone: recruiter.phone || '',
      company_description: recruiter.company_description || '',
      board_company: recruiter.board_company || '',
      corp_scholar_participant: recruiter.corp_scholar_participant || '',
      webinar_host: recruiter.webinar_host || '',
      internship_type: recruiter.internship_type || '',
      internship_description: recruiter.internship_description || '',
      paid_summer_internship: recruiter.paid_summer_internship || '',
      expected_start_date: recruiter.expected_start_date || '',
      internship_length: recruiter.internship_length ? String(recruiter.internship_length) : '',
      housing_provided: recruiter.housing_provided || '',
      internship_salary: recruiter.internship_salary || '',
      salary_type: recruiter.salary_type || '',
      minimum_gpa: recruiter.minimum_gpa ? String(recruiter.minimum_gpa) : '',
      number_of_references: recruiter.number_of_references ? String(recruiter.number_of_references) : '',
      essay_question: recruiter.essay_question || '',
      external_application_link: recruiter.external_application_link || '',
      evergreen_link: recruiter.evergreen_link || '',
      expect_evergreen_link: recruiter.expect_evergreen_link || '',
    });
    
    // Parse and set logo
    if (recruiter.logo) {
      setLogoUrl(recruiter.logo);
    }
    
    // Parse and set locations
    if (recruiter.company_locations) {
      try {
        // Check if it's already an object or needs parsing
        let locations;
        
        if (typeof recruiter.company_locations === 'string') {
          // Handle malformed JSON like {" Massachusetts","Minnesota"}
          let cleanedJson = recruiter.company_locations.trim();
          
          // Check if it's malformed JSON (missing key-value pairs)
          if (cleanedJson.startsWith('{') && cleanedJson.endsWith('}') && !cleanedJson.includes(':')) {
            // This looks like a malformed JSON with just values, try to fix it
            console.warn('Detected malformed company_locations JSON:', cleanedJson);
            
            // Extract state names from malformed JSON
            const stateNames = cleanedJson.slice(1, -1) // Remove braces
              .split(',')
              .map(s => s.trim().replace(/"/g, '')) // Remove quotes and trim
              .filter(s => s.length > 0);
            
            // Convert to proper format: {"State": ["Remote"]} for each state
            locations = {};
            stateNames.forEach(state => {
              locations[state] = ['Remote']; // Default to remote for now
            });
            
            devLog('Converted malformed locations to:', locations);
          } else {
            // Try normal JSON parsing
            locations = JSON.parse(cleanedJson);
          }
        } else {
          locations = recruiter.company_locations;
        }
        
        const states = {};
        const cities = {};
        
        if (locations && typeof locations === 'object') {
          Object.entries(locations).forEach(([state, cityList]) => {
            states[state] = true;
            if (Array.isArray(cityList)) {
              cities[state] = cityList.join(', ');
            } else {
              cities[state] = cityList || '';
            }
          });
        }
        
        setSelectedStates(states);
        setStateCities(cities);
      } catch (e) {
        console.error('Error parsing locations:', e);
        console.error('company_locations value:', recruiter.company_locations);
        
        // If parsing completely fails, try to extract readable information
        if (typeof recruiter.company_locations === 'string') {
          const locStr = recruiter.company_locations.replace(/[{}"/]/g, '').trim();
          if (locStr) {
            const stateNames = locStr.split(',').map(s => s.trim()).filter(s => s.length > 0);
            const states = {};
            const cities = {};
            
            stateNames.forEach(state => {
              states[state] = true;
              cities[state] = 'Remote'; // Default fallback
            });
            
            setSelectedStates(states);
            setStateCities(cities);
            devLog('Fallback parsing successful:', { states, cities });
          }
        }
      }
    }
    
    // Parse and set majors
    if (recruiter.eligible_majors) {
      const majorList = recruiter.eligible_majors.split(',').map(m => m.trim());
      const majorObj = {};
      majorList.forEach(major => {
        majorObj[major] = true;
      });
      setSelectedMajors(majorObj);
    }
    
    // Parse and set institutions
    if (recruiter.preferred_institutions) {
      const instList = recruiter.preferred_institutions.split(';').map(i => i.trim());
      const instObj = {};
      instList.forEach(inst => {
        instObj[inst] = true;
      });
      setSelectedInstitutions(instObj);
    }
    
    // Parse and set class years
    if (recruiter.preferred_class_year) {
      const yearList = recruiter.preferred_class_year.split(',').map(y => y.trim());
      const yearObj = {};
      yearList.forEach(year => {
        yearObj[year] = true;
      });
      setSelectedClassYears(yearObj);
    }
  };

  const loadRecruiterDataById = async (recruiterId) => {
    try {
      const recruiterToken = localStorage.getItem('recruiterToken');
      const response = await axios.get(`${BACKEND_URL}/api/recruiter/profile/${recruiterId}`, {
        headers: {
          'Authorization': `Bearer ${recruiterToken}`
        }
      });
      const recruiter = response.data;

      setIsUpdateMode(true);
      setRecruiterId(recruiter.id);
      populateFormWithRecruiterData(recruiter);

    } catch (error) {
      console.error('Error loading recruiter data:', error);
      setError('Failed to load your profile data. Please try again.');
    } finally {
      setInitialLoading(false);
    }
  };

  const loadRecruiterData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/recruiter/profile?token=${updateToken}`);
      const recruiter = response.data;
      
      setIsUpdateMode(true);
      setRecruiterId(recruiter.id);
      populateFormWithRecruiterData(recruiter);
      
    } catch (err) {
      console.error('Error loading recruiter data:', err);
      setError('Failed to load recruiter data');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleStateToggle = (state) => {
    setSelectedStates(prev => ({
      ...prev,
      [state]: !prev[state]
    }));
    
    // If unchecking, remove cities for that state
    if (selectedStates[state]) {
      setStateCities(prev => {
        const newCities = { ...prev };
        delete newCities[state];
        return newCities;
      });
    }
  };

  const handleCityChange = (state, cities) => {
    setStateCities(prev => ({
      ...prev,
      [state]: cities
    }));
  };

  const handleMajorToggle = (major) => {
    setSelectedMajors(prev => ({
      ...prev,
      [major]: !prev[major]
    }));
  };

  const handleInstitutionToggle = (institution) => {
    setSelectedInstitutions(prev => ({
      ...prev,
      [institution]: !prev[institution]
    }));
  };

  const handleClassYearToggle = (year) => {
    setSelectedClassYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a JPG or PNG file.');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('File size must be less than 5MB.');
        return;
      }
      
      setLogoFile(file);
      setError(null);
      
      // Upload the logo immediately
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post('http://localhost:8000/upload-logo', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        setLogoUrl(response.data.logo_url);
      } catch (err) {
        console.error('Logo upload error:', err);
        setError('Failed to upload logo. Please try again.');
        setLogoFile(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    
    // Prepare location data
    const selectedStatesList = Object.entries(selectedStates)
      .filter(([state, selected]) => selected);
    
    // Validate company description
    if (!formData.company_description || formData.company_description.trim().length < 10) {
      setError('Business/Entity description must be at least 10 characters long.');
      setLoading(false);
      return;
    }
    
    // Validate board company selection
    if (!formData.board_company || !['YES', 'NO'].includes(formData.board_company)) {
      setError('Please select whether this is a NACME Board Company.');
      setLoading(false);
      return;
    }
    
    // Validate corporate scholars participation
    if (!formData.corp_scholar_participant || !['YES', 'NO'].includes(formData.corp_scholar_participant)) {
      setError('Please select whether you will participate in the Corporate Scholars Internship Program.');
      setLoading(false);
      return;
    }
    
    // Validate webinar hosting
    if (!formData.webinar_host || !['YES', 'NO'].includes(formData.webinar_host)) {
      setError('Please select whether you will host a webinar this Fall 2025 / Spring 2026.');
      setLoading(false);
      return;
    }
    
    // Validate internship type
    const allowedInternshipTypes = ['In-person', 'Virtual', 'Hybrid (Virtual and In-person)', 'To be Determined'];
    if (!formData.internship_type || !allowedInternshipTypes.includes(formData.internship_type)) {
      setError('Please select the type of internship.');
      setLoading(false);
      return;
    }
    
    // Validate internship description
    if (!formData.internship_description || formData.internship_description.trim().length < 20) {
      setError('Internship description must be at least 20 characters long.');
      setLoading(false);
      return;
    }
    
    // Validate paid summer internship
    if (!formData.paid_summer_internship || !['YES', 'NO'].includes(formData.paid_summer_internship)) {
      setError('Please select whether this scholarship includes a paid summer internship.');
      setLoading(false);
      return;
    }
    
    // Validate expected start date
    if (!formData.expected_start_date || !formData.expected_start_date.trim()) {
      setError('Expected internship start date is required.');
      setLoading(false);
      return;
    }
    
    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(formData.expected_start_date.trim())) {
      setError('Expected start date must be in YYYY-MM-DD format.');
      setLoading(false);
      return;
    }
    
    // Validate that it's a valid date
    const startDate = new Date(formData.expected_start_date.trim());
    if (isNaN(startDate.getTime())) {
      setError('Please enter a valid date.');
      setLoading(false);
      return;
    }
    
    // Validate internship length
    if (!formData.internship_length || !['9', '10', '11', '12', '13'].includes(formData.internship_length)) {
      setError('Please select the expected length of internship (9-13 weeks).');
      setLoading(false);
      return;
    }
    
    // Validate housing provided
    if (!formData.housing_provided || !['YES', 'NO'].includes(formData.housing_provided)) {
      setError('Please select whether housing is provided.');
      setLoading(false);
      return;
    }
    
    // Validate internship salary
    if (!formData.internship_salary || !formData.internship_salary.trim()) {
      setError('Estimated internship salary is required.');
      setLoading(false);
      return;
    }
    
    // Validate salary type
    const allowedSalaryTypes = ['Per Month', 'Per Week', 'Bi-weekly', 'Per Hour', 'To Be Determined'];
    if (!formData.salary_type || !allowedSalaryTypes.includes(formData.salary_type)) {
      setError('Please select a salary type.');
      setLoading(false);
      return;
    }
    
    // Validate minimum GPA
    const gpaString = String(formData.minimum_gpa || '').trim();
    if (!gpaString) {
      setError('Minimum GPA is required.');
      setLoading(false);
      return;
    }
    
    const gpaValue = parseFloat(gpaString);
    if (isNaN(gpaValue) || gpaValue < 0.0 || gpaValue > 4.0) {
      setError('GPA must be a valid number between 0.0 and 4.0.');
      setLoading(false);
      return;
    }
    
    // Validate eligible majors
    const selectedMajorsList = Object.entries(selectedMajors)
      .filter(([major, selected]) => selected)
      .map(([major]) => major);
    
    if (selectedMajorsList.length === 0) {
      setError('Please select at least one eligible major.');
      setLoading(false);
      return;
    }
    
    // Validate preferred institutions
    const selectedInstitutionsList = Object.entries(selectedInstitutions)
      .filter(([institution, selected]) => selected)
      .map(([institution]) => institution);
    
    if (selectedInstitutionsList.length === 0) {
      setError('Please select at least one preferred institution.');
      setLoading(false);
      return;
    }
    
    // Validate number of references
    if (!formData.number_of_references || !formData.number_of_references.trim()) {
      setError('Number of required references is required.');
      setLoading(false);
      return;
    }
    
    const referencesValue = parseInt(formData.number_of_references.trim(), 10);
    if (isNaN(referencesValue) || referencesValue < 0 || referencesValue > 10) {
      setError('Number of references must be between 0 and 10.');
      setLoading(false);
      return;
    }
    
    // Validate preferred class years
    const selectedClassYearsList = Object.entries(selectedClassYears)
      .filter(([year, selected]) => selected)
      .map(([year]) => year);
    
    if (selectedClassYearsList.length === 0) {
      setError('Please select at least one preferred class year.');
      setLoading(false);
      return;
    }
    
    // Validate external application link
    if (!formData.external_application_link || !formData.external_application_link.trim()) {
      setError('Company application link is required.');
      setLoading(false);
      return;
    }
    
    // Basic URL validation
    const urlPattern = /^https?:\/\/.+/;
    if (!urlPattern.test(formData.external_application_link.trim())) {
      setError('Please enter a valid URL starting with http:// or https://');
      setLoading(false);
      return;
    }
    
    // Validate evergreen link
    if (!formData.evergreen_link || !['YES', 'NO'].includes(formData.evergreen_link)) {
      setError('Please select whether this is an evergreen link.');
      setLoading(false);
      return;
    }
    
    // Validate expect evergreen link (only required if evergreen_link is NO)
    if (formData.evergreen_link === 'NO') {
      if (!formData.expect_evergreen_link || !['YES', 'NO'].includes(formData.expect_evergreen_link)) {
        setError('Please specify whether you will provide an evergreen link by September 2025.');
        setLoading(false);
        return;
      }
    }
    
    // Validate that at least one location is selected
    if (selectedStatesList.length === 0) {
      setError('Please select at least one internship location.');
      setLoading(false);
      return;
    }
    
    // Validate that each selected state has at least one city (except NA/Remote)
    const locationsData = {};
    let hasValidCities = false;
    let hasNARemote = false;
    
    for (const [state] of selectedStatesList) {
      if (state === 'NA/Remote') {
        locationsData[state] = ['Remote'];
        hasValidCities = true;
        hasNARemote = true;
      } else {
        const cities = stateCities[state] || '';
        if (cities.trim()) {
          const cityList = cities.split(',')
            .map(city => city.trim())
            .filter(city => city.length > 0)
            .slice(0, 7); // Limit to 7 cities
          
          if (cityList.length > 0) {
            locationsData[state] = cityList;
            hasValidCities = true;
          }
        }
      }
    }
    
    if (!hasValidCities) {
      setError('Please enter at least one city for your selected states (or select NA/Remote).');
      setLoading(false);
      return;
    }
    
    const submissionData = {
      ...formData,
      company_locations: JSON.stringify(locationsData),
      internship_length: parseInt(formData.internship_length, 10),  // Convert to integer
      eligible_majors: selectedMajorsList.join(','),  // Convert array to comma-separated string
      preferred_institutions: selectedInstitutionsList.join(';'),  // Use semicolon since some names have commas
      number_of_references: parseInt(formData.number_of_references, 10),  // Convert to integer
      essay_question: formData.essay_question.trim() || null,  // Send null if empty
      preferred_class_year: selectedClassYearsList.join(','),  // Convert array to comma-separated string
      logo: logoUrl || null,  // Include logo URL if uploaded
      external_application_link: formData.external_application_link.trim(),  // Company application link
      evergreen_link: formData.evergreen_link,  // Evergreen link status
      expect_evergreen_link: formData.evergreen_link === 'NO' ? formData.expect_evergreen_link : null  // Only include if evergreen_link is NO
    };
    
    devLogSafe('Submitting data', submissionData);
    
    try {
      if (isUpdateMode) {
        // Update existing recruiter
        const recruiterToken = localStorage.getItem('recruiterToken');
        await axios.put(`${BACKEND_URL}/api/recruiter/profile/${recruiterId}`, submissionData, {
          headers: {
            'Authorization': recruiterToken ? `Bearer ${recruiterToken}` : undefined
          }
        });
        
        // Confirm profile if using token
        if (updateToken) {
          await axios.put(`${BACKEND_URL}/api/recruiter/confirm-profile`, {
            token: updateToken
          });
        }
        
        // Redirect to dashboard or show success
        navigate('/recruiter/dashboard');
      } else {
        // New registration
        await axios.post(`${BACKEND_URL}/register-recruiter`, submissionData);
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Registration error:', err);
      if (err.response) {
        console.error('Error response:', err.response.data);
        console.error('Error status:', err.response.status);
      }
      setError(err.response?.data?.detail || 'Submission failed. Please check the console for more details.');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (name, label, type = 'text') => {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} <span className="text-red-500">*</span>
        </label>
        <input
          type={type}
          name={name}
          id={name}
          value={formData[name]}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
          placeholder={`Enter ${label.toLowerCase()}`}
        />
      </div>
    );
  };

  const renderTextArea = (name, label, rows = 4, minChars = 10) => {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} <span className="text-red-500">*</span>
        </label>
        <textarea
          name={name}
          id={name}
          rows={rows}
          value={formData[name]}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 resize-vertical"
          placeholder={`Enter ${label.toLowerCase()}`}
        />
        <p className="text-xs text-gray-500 mt-1">
          Minimum {minChars} characters required
        </p>
      </div>
    );
  };

  const renderSelect = (name, label, options) => {
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} <span className="text-red-500">*</span>
        </label>
        <select
          name={name}
          id={name}
          value={formData[name]}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
        >
          <option value="">Please select...</option>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your information...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-4xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Registration Successful!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for registering. Your request has been received and is pending approval.
            You will receive an email once your account has been approved by an administrator.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">
{(isUpdateMode || isLoggedInRecruiter) ? 'Update Your Profile' : 'Recruiter Registration'}
      </h1>
{(isUpdateMode || isLoggedInRecruiter) && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800">
            {isLoggedInRecruiter 
              ? 'Welcome! Please review and update your profile information below.'
              : 'Please review and update your information below. Changes will be saved when you submit the form.'
            }
          </p>
        </div>
      )}

      {!isUpdateMode && !isLoggedInRecruiter && (
        <div className="mb-6">
          <p className="text-gray-600">
            Please fill out the form below to register as a recruiter. Your registration will be reviewed by an administrator before approval.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="md:col-span-2">
            {renderField('full_name', 'Full Name')}
          </div>
          
          <div className="md:col-span-2">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                disabled={isUpdateMode || isLoggedInRecruiter}
                required
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 ${
                  (isUpdateMode || isLoggedInRecruiter) ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                placeholder="Enter email address"
              />
              {(isUpdateMode || isLoggedInRecruiter) && (
                <p className="text-xs text-blue-600 mt-1">
                  Email cannot be changed when updating your profile
                </p>
              )}
            </div>
          </div>
          
          <div>
            {renderField('organization', 'Organization/Company')}
          </div>
          
          <div>
            {renderField('title', 'Job Title')}
          </div>
          
          <div className="md:col-span-2">
            {renderField('phone', 'Phone Number', 'tel')}
          </div>
          
          <div className="md:col-span-2">
            {renderTextArea('company_description', 'Business/Entity Description', 5)}
          </div>
          
          <div className="md:col-span-2">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Logo Upload
              </label>
              <p className="text-xs text-gray-600 mb-2">
                Upload your company logo (JPG or PNG, max 5MB). This field is optional.
              </p>
              <div className="space-y-2">
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleLogoChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                />
                {logoFile && (
                  <div className="flex items-center space-x-2 text-sm text-green-600">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Logo uploaded: {logoFile.name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="md:col-span-2">
            {renderSelect('board_company', 'Is this a NACME Board Company?', [
              { value: 'YES', label: 'Yes' },
              { value: 'NO', label: 'No' }
            ])}
          </div>
          
          <div className="md:col-span-2">
            {renderSelect('corp_scholar_participant', 'Will you participate in the Corporate Scholars Internship Program (Summer 2026)?', [
              { value: 'YES', label: 'Yes' },
              { value: 'NO', label: 'No' }
            ])}
          </div>
          
          <div className="md:col-span-2">
            {renderSelect('webinar_host', 'Will you host a webinar this Fall 2025 / Spring 2026?', [
              { value: 'YES', label: 'Yes' },
              { value: 'NO', label: 'No' }
            ])}
          </div>
          
          <div className="md:col-span-2">
            {renderSelect('internship_type', 'Type of Internship', [
              { value: 'In-person', label: 'In-person' },
              { value: 'Virtual', label: 'Virtual' },
              { value: 'Hybrid (Virtual and In-person)', label: 'Hybrid (Virtual and In-person)' },
              { value: 'To be Determined', label: 'To be Determined' }
            ])}
          </div>
          
          <div className="md:col-span-2">
            {renderTextArea('internship_description', 'Internship(s) Description', 6, 20)}
          </div>
          
          <div className="md:col-span-2">
            {renderSelect('paid_summer_internship', 'Does this scholarship include a paid summer internship?', [
              { value: 'YES', label: 'Yes' },
              { value: 'NO', label: 'No' }
            ])}
          </div>
          
          <div className="md:col-span-2">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expected Internship Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="expected_start_date"
                id="expected_start_date"
                value={formData.expected_start_date}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
              />
              <p className="text-xs text-gray-500 mt-1">
                If you don't know the exact date, set the "day" to the 1st of the starting month.
              </p>
            </div>
          </div>
          
          <div className="md:col-span-2">
            {renderSelect('internship_length', 'Expected Length of Internship (in weeks - minimum of 9 - maximum of 13)', [
              { value: '9', label: '9 weeks' },
              { value: '10', label: '10 weeks' },
              { value: '11', label: '11 weeks' },
              { value: '12', label: '12 weeks' },
              { value: '13', label: '13 weeks' }
            ])}
          </div>
          
          <div className="md:col-span-2">
            {renderSelect('housing_provided', 'Housing Provided?', [
              { value: 'YES', label: 'Yes' },
              { value: 'NO', label: 'No' }
            ])}
          </div>
          
          <div className="md:col-span-2">
            {renderField('internship_salary', 'Estimated Internship Salary (Please enter TBD if unknown at this time.)')}
          </div>
          
          <div className="md:col-span-2">
            {renderSelect('salary_type', 'Salary Type', [
              { value: 'Per Month', label: 'Per Month' },
              { value: 'Per Week', label: 'Per Week' },
              { value: 'Bi-weekly', label: 'Bi-weekly' },
              { value: 'Per Hour', label: 'Per Hour' },
              { value: 'To Be Determined', label: 'To Be Determined' }
            ])}
          </div>
          
          <div className="md:col-span-2">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                What is the required minimum GPA to apply for the internship? <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="minimum_gpa"
                id="minimum_gpa"
                value={formData.minimum_gpa}
                onChange={handleChange}
                required
                min="0.0"
                max="4.0"
                step="0.01"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                placeholder="Enter GPA (0.0 - 4.0)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter a value between 0.0 and 4.0
              </p>
            </div>
          </div>
          
          <div className="md:col-span-2">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Eligible Majors (If multiple, select each one) <span className="text-red-500">*</span>
              </label>
              <div className="border rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto">
                <div className="mb-3 pb-3 border-b border-gray-200">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={Object.keys(selectedMajors).length === majors.length && majors.every(major => selectedMajors[major])}
                      onChange={() => {
                        const allSelected = Object.keys(selectedMajors).length === majors.length && majors.every(major => selectedMajors[major]);
                        const newSelection = {};
                        if (!allSelected) {
                          majors.forEach(major => { newSelection[major] = true; });
                        }
                        setSelectedMajors(newSelection);
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Select All</span>
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {majors.map(major => (
                    <label key={major} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedMajors[major] || false}
                        onChange={() => handleMajorToggle(major)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{major}</span>
                    </label>
                  ))}
                </div>
                {Object.keys(selectedMajors).filter(major => selectedMajors[major]).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700">Selected majors:</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {Object.entries(selectedMajors)
                        .filter(([major, selected]) => selected)
                        .map(([major]) => major)
                        .join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="md:col-span-2">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferred NACME Partner Institutions (If multiple, select each one.) <span className="text-red-500">*</span>
              </label>
              <div className="border rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto">
                <div className="mb-3 pb-3 border-b border-gray-200">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={Object.keys(selectedInstitutions).length === institutions.length && institutions.every(inst => selectedInstitutions[inst])}
                      onChange={() => {
                        const allSelected = Object.keys(selectedInstitutions).length === institutions.length && institutions.every(inst => selectedInstitutions[inst]);
                        const newSelection = {};
                        if (!allSelected) {
                          institutions.forEach(inst => { newSelection[inst] = true; });
                        }
                        setSelectedInstitutions(newSelection);
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Select All</span>
                  </label>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {institutions.map(institution => (
                    <label key={institution} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedInstitutions[institution] || false}
                        onChange={() => handleInstitutionToggle(institution)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{institution}</span>
                    </label>
                  ))}
                </div>
                {Object.keys(selectedInstitutions).filter(inst => selectedInstitutions[inst]).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700">Selected institutions:</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {Object.entries(selectedInstitutions)
                        .filter(([institution, selected]) => selected)
                        .map(([institution]) => institution)
                        .join('; ')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="md:col-span-2">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of required references <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="number_of_references"
                id="number_of_references"
                value={formData.number_of_references}
                onChange={handleChange}
                required
                min="0"
                max="10"
                step="1"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                placeholder="Enter number (0-10)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter a value between 0 and 10
              </p>
            </div>
          </div>
          
          <div className="md:col-span-2">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Application Essay Question (Optional)
              </label>
              <p className="text-xs text-gray-600 mb-2">
                If you would like to require an essay for interns to answer, please list the essay question(s) below.
              </p>
              <textarea
                name="essay_question"
                id="essay_question"
                rows={4}
                value={formData.essay_question}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 resize-vertical"
                placeholder="Enter your essay question(s) here..."
              />
              <p className="text-xs text-gray-500 mt-1">
                This field is optional. Leave blank if you don't require an essay.
              </p>
            </div>
          </div>
          
          <div className="md:col-span-2">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company's Application Link <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-600 mb-2">
                Please enter the website URL where students should apply for roles.
              </p>
              <input
                type="url"
                name="external_application_link"
                id="external_application_link"
                value={formData.external_application_link}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                placeholder="https://example.com/careers/apply"
              />
              <p className="text-xs text-gray-500 mt-1">
                Must be a valid URL starting with http:// or https://
              </p>
            </div>
          </div>
          
          <div className="md:col-span-2">
            {renderSelect('evergreen_link', 'Is this an \'evergreen\' link for tracking applicants that we may reuse in subsequent years?', [
              { value: 'YES', label: 'Yes' },
              { value: 'NO', label: 'No' }
            ])}
          </div>
          
          {formData.evergreen_link === 'NO' && (
            <div className="md:col-span-2">
              {renderSelect('expect_evergreen_link', 'Will you provide an evergreen link for applicants by September 2025?', [
                { value: 'YES', label: 'Yes' },
                { value: 'NO', label: 'No' }
              ])}
            </div>
          )}
          
          <div className="md:col-span-2">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferred Class Year of Applicants <span className="text-red-500">*</span>
              </label>
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {classYears.map(year => (
                    <label key={year} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedClassYears[year] || false}
                        onChange={() => handleClassYearToggle(year)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{year}</span>
                    </label>
                  ))}
                </div>
                {Object.keys(selectedClassYears).filter(year => selectedClassYears[year]).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700">Selected class years:</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {Object.entries(selectedClassYears)
                        .filter(([year, selected]) => selected)
                        .map(([year]) => year)
                        .join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Internship Locations Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Internship Locations <span className="text-red-500">*</span>
            </h3>
            <button
              type="button"
              onClick={() => setShowLocations(!showLocations)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              {showLocations ? 'Hide' : 'Add'} Locations
            </button>
          </div>
          
          {showLocations && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <p className="text-sm text-gray-600 mb-4">
                <span className="font-medium text-red-600">Required:</span> Select the states where you have internship opportunities and specify the cities for each selected state.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {states.map(state => (
                  <div key={state} className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedStates[state] || false}
                        onChange={() => handleStateToggle(state)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">{state}</span>
                    </label>
                    
                    {selectedStates[state] && state !== 'NA/Remote' && (
                      <input
                        type="text"
                        placeholder="Enter cities (comma-separated)"
                        value={stateCities[state] || ''}
                        onChange={(e) => handleCityChange(state, e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 border-gray-300"
                      />
                    )}
                  </div>
                ))}
              </div>
              
              {Object.keys(selectedStates).filter(state => selectedStates[state]).length > 0 ? (
                <div className="mt-4 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm font-medium text-blue-800">Selected Locations:</p>
                  <ul className="mt-2 space-y-1">
                    {Object.entries(selectedStates)
                      .filter(([state, selected]) => selected)
                      .map(([state]) => (
                        <li key={state} className="text-sm text-blue-700">
                          <strong>{state}:</strong> {state === 'NA/Remote' ? 'Remote opportunities available' : (stateCities[state] || 'No cities specified')}
                        </li>
                      ))}
                  </ul>
                </div>
              ) : showLocations && (
                <div className="mt-4 p-3 bg-red-50 rounded-md border border-red-200">
                  <p className="text-sm font-medium text-red-800">
                    ⚠️ No locations selected. Please select at least one state and specify cities.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-2 rounded-md font-medium ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            } text-white`}
          >
            {loading ? 'Submitting...' : (isUpdateMode ? 'Update Profile' : 'Submit Registration')}
          </button>
        </div>
      </form>

      {error && (
        <div className="mt-6 p-4 rounded-md bg-red-100 text-red-700">
          {error}
        </div>
      )}
    </div>
  );
};

export default RecruiterRegistrationForm;
